import React, { useState, useEffect } from 'react';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { isValidPhoneNumber } from 'libphonenumber-js';
import validator from 'validator';
import { Typography } from '../typography';
import { Button } from '../button';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { i18n } from '../../i18n';
import { Messenger } from '../../types';
import { getPhoneNumberValue } from '../../utils';
import {
  clientCategoriesV2,
  INPUT_ERRORS,
  messengerOptions,
  REGEX,
  CountryOptions,
  StateOptions,
} from '../../const';
import { Input, InputPhoneNumber } from '../input';
import { SearchableSelect, Select } from '../select';
import { useIsSmallScreen, useSendSignUpMailerMutation } from '../../hooks';

export const sendSignUpModal: any = {};

export const SendSignUpModal = () => {
  const { mutate } = useSendSignUpMailerMutation();
  const isSmallScreen = useIsSmallScreen();
  const [open, setOpen] = useState(false);

  const [companyLegalName, setCompanyLegalName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [contactPersonFirstName, setContactPersonFirstName] = useState('');
  const [contactPersonLastName, setContactPersonLastName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState(undefined);

  const [messengers, setMessengers] = useState<Messenger[]>([{ label: '', identifier: '' }]);
  const [category, setCategory] = useState('Other');

  const [isSubmitClicked, setIsSubmitClicked] = useState<boolean>(false);
  const [updateKey, setUpdate] = useState<any>(Math.random());

  const validators = {
    companyLegalName: companyLegalName
      ? !validator.isLength(companyLegalName, { min: 4, max: 100 })
        ? INPUT_ERRORS.NOT_VALID.COMPANY_LEGAL_NAME_LENGTH
        : !REGEX.COMPANY_NAME.test(companyLegalName)
        ? INPUT_ERRORS.NOT_VALID.COMPANY_LEGAL_NAME
        : ''
      : true,
    streetAddress: streetAddress
      ? !validator.isLength(streetAddress, { min: 5, max: 100 })
        ? INPUT_ERRORS.NOT_VALID.STREET_ADDRESS_LENGTH
        : !REGEX.STREET_ADDRESS.test(streetAddress)
        ? INPUT_ERRORS.NOT_VALID.STREET_ADDRESS
        : ''
      : true,
    city: city
      ? !validator.isLength(city, { min: 1, max: 85 })
        ? INPUT_ERRORS.NOT_VALID.CITY_LENGTH
        : !REGEX.CITY.test(city)
        ? INPUT_ERRORS.NOT_VALID.CITY
        : ''
      : true,
    country: country
      ? !validator.isLength(country, { min: 1, max: 85 })
        ? INPUT_ERRORS.NOT_VALID.COUNTRY_LENGTH
        : !REGEX.COUNTRY.test(country)
        ? INPUT_ERRORS.NOT_VALID.COUNTRY
        : ''
      : true,
    contactPersonFirstName: contactPersonFirstName
      ? !validator.isLength(contactPersonFirstName, { min: 3 })
        ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_FIRST_NAME_LENGTH
        : !REGEX.USER_NAME.test(contactPersonFirstName)
        ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_FIRST_NAME
        : ''
      : true,
    contactPersonLastName: contactPersonLastName
      ? !validator.isLength(contactPersonLastName, { min: 3 })
        ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_LAST_NAME_LENGTH
        : !REGEX.USER_NAME.test(contactPersonLastName)
        ? INPUT_ERRORS.NOT_VALID.CONTACT_PERSON_LAST_NAME
        : ''
      : true,
    companyEmail: companyEmail
      ? !validator.isLength(companyEmail, { min: 6, max: 50 })
        ? INPUT_ERRORS.NOT_VALID.COMPANY_EMAIL_LENGTH
        : !REGEX.EMAIL.test(companyEmail)
        ? INPUT_ERRORS.NOT_VALID.COMPANY_EMAIL
        : ''
      : true,
    companyPhoneNumber: companyPhoneNumber
      ? !isValidPhoneNumber(`+${getPhoneNumberValue(companyPhoneNumber)}`)
        ? INPUT_ERRORS.NOT_VALID.COMPANY_PHONE_NUMBER
        : ''
      : true,
    messengers: messengers.map((item) => ({
      label: item.label
        ? !validator.isLength(item.label, { max: 30 })
          ? INPUT_ERRORS.NOT_VALID.MESSENGER_NAME_LENGTH
          : ''
        : '',
      identifier: item.identifier
        ? !validator.isLength(item.identifier, { max: 100 })
          ? INPUT_ERRORS.NOT_VALID.MESSENGER_IDENTIFIER_LENGTH
          : ''
        : '',
    })),
  };

  useEffect(() => {
    sendSignUpModal.open = () => {
      setCompanyLegalName('');
      setStreetAddress('');
      setCity('');
      setCountry('');
      setContactPersonFirstName('');
      setContactPersonLastName('');
      setCompanyEmail('');
      setCompanyPhoneNumber(undefined);
      setMessengers([{ label: '', identifier: '' }]);
      setCategory('Other');

      setUpdate(Math.random());
      setIsSubmitClicked(false);
      setOpen(true);
    };
  }, []);

  const handleAddMessengerClick = () => {
    setMessengers([...messengers, { label: '', identifier: '' }]);
  };

  const handleDeleteClick = (i: number) => {
    setMessengers(messengers.filter((_: any, index: number) => index !== i));

    setUpdate(Math.random());
  };

  const handleOnClose = () => {
    setOpen(false);
  };

  const handleSubmitClick = async () => {
    setIsSubmitClicked(true);
    setUpdate(Math.random());

    const { messengers: validatorMessengers, ...validatorProps } = validators;

    if (
      [
        !validatorMessengers.some((item: any) => !!item.label || !!item.identifier),
        ...Object.values(validatorProps).map((val) => !val),
      ].some((val) => !val)
    )
      return;

    const otherProps: any = {
      category,
    };

    mutate(
      {
        companyLegalName,
        streetAddress,
        city,
        country,
        contactPersonFirstName,
        contactPersonLastName,
        companyEmail,
        companyPhoneNumber: getPhoneNumberValue(companyPhoneNumber),
        messengers,
        containerNumber: sendSignUpModal.number,
        price: sendSignUpModal.price,
        ...otherProps,
      },
      {
        onSuccess: () => {
          setOpen(false);
          sendSignUpModal.submitClick();
        },
        onError: () => {
          if (process.env.NODE_ENV === 'development') {
            setOpen(false);
            sendSignUpModal.submitClick();
          }
        },
      },
    );
  };

  return (
    <ModalBase
      id={'send-sign-up-modal'}
      paperSize={isSmallScreen ? 'mobile' : 'default'}
      open={open}
      onClose={handleOnClose}
    >
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.sendSignUpTitle')}
        </Typography>
        <ModalBodyCustomColumns columns={isSmallScreen ? '1fr' : '1fr 1fr'}>
          <Input
            defaultValue={companyLegalName}
            onChange={(e: any) => setCompanyLegalName(e.target.value)}
            label={i18n.t('label.companyLegalName') + '*'}
            type={'text'}
            data-size={'medium'}
            error={
              (isSubmitClicked && !companyLegalName) ||
              (!!companyLegalName && validators.companyLegalName)
            }
            helperText={
              isSubmitClicked && !companyLegalName
                ? INPUT_ERRORS.REQUIRED.COMPANY_LEGAL_NAME
                : validators.companyLegalName
            }
          />
          <SearchableSelect
            id={i18n.t('label.country') + '*'}
            selectedOptionLabel={country}
            label={i18n.t('label.country') + '*'}
            options={CountryOptions}
            onChange={(e: any) => {
              setCity('');
              setCountry(e.target.value);
            }}
            error={(isSubmitClicked && !country) || (!!country && !!validators.country)}
            helperText={
              isSubmitClicked && !country ? INPUT_ERRORS.REQUIRED.COUNTRY : validators.country
            }
          />
          <SearchableSelect
            id={i18n.t('label.city') + '*'}
            selectedOptionLabel={city}
            label={i18n.t('label.city') + '*'}
            options={StateOptions(country)}
            onChange={(e: any) => setCity(e.target.value)}
            error={(isSubmitClicked && !city) || (!!city && !!validators.city)}
            helperText={isSubmitClicked && !city ? INPUT_ERRORS.REQUIRED.CITY : validators.city}
            disabled={!country}
          />
          <Input
            defaultValue={streetAddress}
            onChange={(e: any) => setStreetAddress(e.target.value)}
            label={i18n.t('label.streetAddress') + '*'}
            type={'text'}
            data-size={'medium'}
            error={
              (isSubmitClicked && !streetAddress) || (!!streetAddress && validators.streetAddress)
            }
            helperText={
              isSubmitClicked && !streetAddress
                ? INPUT_ERRORS.REQUIRED.STREET_ADDRESS
                : validators.streetAddress
            }
          />
          <Input
            defaultValue={companyEmail}
            onChange={(e: any) => setCompanyEmail(e.target.value)}
            label={i18n.t('label.companyEmail') + '*'}
            type={'text'}
            data-size={'medium'}
            withoutSpace={true}
            error={
              (isSubmitClicked && !companyEmail) || (!!companyEmail && validators.companyEmail)
            }
            helperText={
              isSubmitClicked && !companyEmail
                ? INPUT_ERRORS.REQUIRED.COMPANY_EMAIL
                : validators.companyEmail
            }
          />
          <InputPhoneNumber
            value={companyPhoneNumber}
            onChange={(e: any) => setCompanyPhoneNumber(e.target.value)}
            label={i18n.t('label.companyPhoneNumber') + '*'}
            data-size={'medium'}
            error={
              (isSubmitClicked && !companyPhoneNumber) ||
              (!!companyPhoneNumber && validators.companyPhoneNumber)
            }
            helperText={
              isSubmitClicked && !companyPhoneNumber
                ? INPUT_ERRORS.REQUIRED.COMPANY_PHONE_NUMBER
                : validators.companyPhoneNumber
            }
          />
          <Input
            defaultValue={contactPersonFirstName}
            onChange={(e: any) => setContactPersonFirstName(e.target.value)}
            label={i18n.t('label.contactPersonFirstName') + '*'}
            type={'text'}
            data-size={'medium'}
            error={
              (isSubmitClicked && !contactPersonFirstName) ||
              (!!contactPersonFirstName && validators.contactPersonFirstName)
            }
            helperText={
              isSubmitClicked && !contactPersonFirstName
                ? INPUT_ERRORS.REQUIRED.CONTACT_PERSON_FIRST_NAME
                : validators.contactPersonFirstName
            }
          />
          <Input
            defaultValue={contactPersonLastName}
            onChange={(e: any) => setContactPersonLastName(e.target.value)}
            label={i18n.t('label.contactPersonLastName') + '*'}
            type={'text'}
            data-size={'medium'}
            error={
              (isSubmitClicked && !contactPersonLastName) ||
              (!!contactPersonLastName && validators.contactPersonLastName)
            }
            helperText={
              isSubmitClicked && !contactPersonLastName
                ? INPUT_ERRORS.REQUIRED.CONTACT_PERSON_LAST_NAME
                : validators.contactPersonLastName
            }
          />
          {isSmallScreen &&
            messengers.map((_: any, i: number) => (
              <React.Fragment key={updateKey + i}>
                <SearchableSelect
                  id={i18n.t('label.messengerName') + i}
                  selectedOptionLabel={messengers[i].label}
                  label={i18n.t('label.messengerName')}
                  queryFn={(inputValue) => {
                    const isNewOption = !messengerOptions.filter((item) =>
                      item.label.toLowerCase().includes(inputValue.toLowerCase()),
                    ).length;
                    if (isNewOption) {
                      return () => [{ label: inputValue, value: inputValue }];
                    }

                    return () => messengerOptions;
                  }}
                  queryKey={'messengers'}
                  selectedAreasEqaul={{ key: 'value', keys: [messengers[i].label] }}
                  onChange={(e: any) => {
                    const newState = messengers;
                    newState[i].label = e.target.value;
                    setMessengers([...newState]);
                  }}
                  error={isSubmitClicked && !!validators.messengers[i].label}
                  helperText={isSubmitClicked && validators.messengers[i].label}
                />
                <ModalBodyCustomColumns key={updateKey + i} columns={'1fr max-content'}>
                  <Input
                    id={i18n.t('label.messengerIdentifier') + i}
                    label={i18n.t('label.messengerIdentifier')}
                    defaultValue={messengers[i].identifier}
                    onChange={(e: any) => {
                      const newState = messengers;
                      newState[i].identifier = e.target.value;
                      setMessengers([...newState]);
                    }}
                    type={'text'}
                    data-size={'medium'}
                    error={isSubmitClicked && !!validators.messengers[i].identifier}
                    helperText={isSubmitClicked && validators.messengers[i].identifier}
                  />
                  {i === messengers.length - 1 ? (
                    <Button
                      className={'row-button-square'}
                      size={'medium'}
                      data-color={'secondary'}
                      isSquare={true}
                      onClick={handleAddMessengerClick}
                    >
                      <AddIcon />
                    </Button>
                  ) : (
                    <Button
                      className={'row-button-square'}
                      size={'medium'}
                      data-color={'destructiveSecondary'}
                      isSquare={true}
                      onClick={() => handleDeleteClick(i)}
                    >
                      <DeleteIcon />
                    </Button>
                  )}
                </ModalBodyCustomColumns>
              </React.Fragment>
            ))}
        </ModalBodyCustomColumns>
        {!isSmallScreen &&
          messengers.map((_: any, i: number) => (
            <ModalBodyCustomColumns
              key={updateKey + i}
              columns={'calc(100%/2 - 2.8rem*(1/2)) 1fr max-content'}
            >
              <SearchableSelect
                id={i18n.t('label.messengerName') + i}
                selectedOptionLabel={messengers[i].label}
                label={i18n.t('label.messengerName')}
                queryFn={(inputValue) => {
                  const isNewOption = !messengerOptions.filter((item) =>
                    item.label.toLowerCase().includes(inputValue.toLowerCase()),
                  ).length;
                  if (isNewOption) {
                    return () => [{ label: inputValue, value: inputValue }];
                  }

                  return () => messengerOptions;
                }}
                queryKey={'messengers'}
                selectedAreasEqaul={{ key: 'value', keys: [messengers[i].label] }}
                onChange={(e: any) => {
                  const newState = messengers;
                  newState[i].label = e.target.value;
                  setMessengers([...newState]);
                }}
                error={isSubmitClicked && !!validators.messengers[i].label}
                helperText={isSubmitClicked && validators.messengers[i].label}
              />
              <Input
                id={i18n.t('label.messengerIdentifier') + i}
                label={i18n.t('label.messengerIdentifier')}
                defaultValue={messengers[i].identifier}
                onChange={(e: any) => {
                  const newState = messengers;
                  newState[i].identifier = e.target.value;
                  setMessengers([...newState]);
                }}
                type={'text'}
                data-size={'medium'}
                error={isSubmitClicked && !!validators.messengers[i].identifier}
                helperText={isSubmitClicked && validators.messengers[i].identifier}
              />
              {i === messengers.length - 1 ? (
                <Button
                  className={'row-button-square'}
                  size={'medium'}
                  data-color={'secondary'}
                  isSquare={true}
                  onClick={handleAddMessengerClick}
                >
                  <AddIcon />
                </Button>
              ) : (
                <Button
                  className={'row-button-square'}
                  size={'medium'}
                  data-color={'destructiveSecondary'}
                  isSquare={true}
                  onClick={() => handleDeleteClick(i)}
                >
                  <DeleteIcon />
                </Button>
              )}
            </ModalBodyCustomColumns>
          ))}
        {sendSignUpModal.withCategory && (
          <Select
            label={i18n.t('label.category')}
            options={clientCategoriesV2}
            value={category}
            onChange={(e: any) => setCategory(e.target.value)}
          />
        )}
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
            {i18n.t('button.submit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
